import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../Utils/Function';
import { FaLock, FaUserShield, FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa';
import BackgroundEffect from '../components/BackgroundEffect';

const AdminLogin = () => {
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // Forgot Password States
    const [showForgot, setShowForgot] = useState(false);
    const [forgotStep, setForgotStep] = useState(1);
    const [forgotPhone, setForgotPhone] = useState('');
    const [forgotOtp, setForgotOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPsw, setConfirmNewPsw] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotError, setForgotError] = useState('');
    const [forgotSuccess, setForgotSuccess] = useState('');

    const resetForgotModal = () => {
        setForgotStep(1);
        setForgotPhone('');
        setForgotOtp('');
        setNewPassword('');
        setConfirmNewPsw('');
        setForgotError('');
        setForgotSuccess('');
        setForgotLoading(false);
    };

    const handleRequestOtp = async () => {
        setForgotLoading(true);
        setForgotError('');
        try {
            const res = await axios.post(`${BASE_URL}/users/requestResetOtp.php`, { mobile_no: forgotPhone });
            if (res.data.status === 200) {
                setForgotStep(2);
            } else {
                setForgotError(res.data.message || 'Verification initialization failed');
            }
        } catch (err) {
            setForgotError(err.response?.data?.message || 'Identity verification offline');
        } finally {
            setForgotLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        setForgotLoading(true);
        setForgotError('');
        try {
            const res = await axios.post(`${BASE_URL}/users/verifyResetOtp.php`, { 
                mobile_no: forgotPhone,
                otp_code: forgotOtp 
            });
            if (res.data.status === 200) {
                setForgotStep(3);
            } else {
                setForgotError(res.data.message || 'Authorization failed');
            }
        } catch (err) {
            setForgotError(err.response?.data?.message || 'Authorization link unstable');
        } finally {
            setForgotLoading(false);
        }
    };

    const handleResetPassword = async () => {
        setForgotLoading(true);
        setForgotError('');
        try {
            const res = await axios.post(`${BASE_URL}/users/resetPassword.php`, { 
                mobile_no: forgotPhone,
                otp_code: forgotOtp,
                new_password: newPassword
            });
            if (res.data.status === 200) {
                setForgotSuccess('Credentials restructured successfully');
                setTimeout(() => {
                    setShowForgot(false);
                    setPhone(forgotPhone);
                    setPassword('');
                }, 2000);
            } else {
                setForgotError(res.data.message || 'Restructuring failed');
            }
        } catch (err) {
            setForgotError(err.response?.data?.message || 'Access key update failed');
        } finally {
            setForgotLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const response = await axios.post(`${BASE_URL}/users/login.php`, {
                mobile_no: phone,
                user_password: password
            });

            if (response.data.status === 200) {
                const userData = response.data.data;
                if (userData.role === "2" || userData.role === 2) {
                    // Store admin user data and the newly added JWT token
                    localStorage.setItem('adminUser', JSON.stringify(userData));
                    if (response.data.token) {
                        localStorage.setItem('adminToken', response.data.token);
                    }
                    navigate('/admin/dashboard');
                } else {
                    setError('Access Denied: Administrative privileges required');
                }
            } else {
                setError(response.data.message || 'Invalid credentials');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'System authentication failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="admin-login-wrapper">
            <BackgroundEffect />
            <div className="admin-login-card">
                <div className="text-center mb-5">
                    <span className="admin-logo-text" style={{ 
                        fontWeight: '700', 
                        color: '#fff', 
                        letterSpacing: '4px',
                        fontSize: 'clamp(1.2rem, 6vw, 2rem)',
                        textTransform: 'uppercase',
                        fontFamily: 'Outfit, sans-serif',
                        display: 'block',
                        marginBottom: '6px',
                        whiteSpace: 'nowrap'
                    }}>
                        AL WASEEQ DESIGN
                    </span>
                    <span className="admin-tagline" style={{
                        fontSize: '0.7rem',
                        color: '#fff',
                        letterSpacing: '3px',
                        fontWeight: '300',
                        textTransform: 'uppercase',
                        opacity: '0.6',
                        display: 'block',
                        marginBottom: '25px'
                    }}>
                        We Craft Your Dream World
                    </span>
                    <div className="admin-badge">
                        <FaUserShield className="me-2 text-secondary-color" />
                        SECURE ADMINISTRATIVE GATEWAY
                    </div>
                </div>

                <form onSubmit={handleLogin}>
                    <div className="mb-4">
                        <label className="admin-label mb-2">MOBILE ACCESS ID</label>
                        <div className="auth-input-wrapper">
                            <input
                                type="tel"
                                className="auth-input"
                                placeholder="Enter your mobile number"
                                value={phone}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    if (val.length <= 10) setPhone(val);
                                }}
                                required
                            />
                        </div>
                    </div>

                    <div className="mb-5">
                        <label className="admin-label mb-2">CREDENTIAL SECURITY KEY</label>
                        <div className="auth-input-wrapper">
                            <input
                                type={showPassword ? "text" : "password"}
                                className="auth-input"
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    {error && <div className="admin-error-box mb-4">{error}</div>}

                    <button
                        type="submit"
                        className="btn-admin-submit w-100 py-3 mb-4"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <span className="spinner-border spinner-border-sm me-2"></span>
                        ) : (
                            <FaLock className="me-2" />
                        )}
                        AUTHORIZE ACCESS
                    </button>

                    <div className="text-center mb-3">
                        <button 
                            type="button" 
                            className="btn-forgot-link"
                            onClick={() => {
                                resetForgotModal();
                                setShowForgot(true);
                            }}
                        >
                            Forgot Password?
                        </button>
                    </div>

                    <div className="text-center">
                        <span className="system-status-indicator">SYSTEM STATUS: OPTIMAL</span>
                    </div>
                </form>
            </div>

            {/* Forgot Password Modal */}
            {showForgot && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content forgot-modal">
                        <div className="modal-header-custom">
                            <h4 className="m-0">CREDENTIAL RECOVERY</h4>
                            <button className="btn-close-modal" onClick={() => { setShowForgot(false); resetForgotModal(); }}><FaTimes /></button>
                        </div>
                        <div className="modal-body-custom">
                            
                            {forgotStep === 1 && (
                                <div className="fade-in">
                                    <h5 className="text-white mb-4 fw-light">STEP 01: IDENTITY VERIFICATION</h5>
                                    <p className="text-white-50 small mb-4">Enter your registered mobile access ID to receive a security authorization code.</p>
                                    <div className="mb-4">
                                        <label className="admin-label mb-2">MOBILE ACCESS ID</label>
                                        <div className="auth-input-wrapper">
                                            <input
                                                type="tel"
                                                className="auth-input"
                                                placeholder="XXXXXXXXXX"
                                                value={forgotPhone}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '');
                                                    if (val.length <= 10) setForgotPhone(val);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {forgotError && <div className="admin-error-box mb-4">{forgotError}</div>}
                                    <button 
                                        className="btn-admin-submit w-100 py-3" 
                                        onClick={handleRequestOtp}
                                        disabled={forgotLoading || forgotPhone.length < 10}
                                    >
                                        {forgotLoading ? 'TRANSMITTING...' : 'REQUEST SECURITY CODE'}
                                    </button>
                                </div>
                            )}

                            {forgotStep === 2 && (
                                <div className="fade-in">
                                    <h5 className="text-white mb-4 fw-light">STEP 02: SECURITY AUTHORIZATION</h5>
                                    <p className="text-white-50 small mb-4">
                                        A 4-digit security code has been sent to&nbsp;
                                        <strong className="text-white">{forgotPhone}</strong>.
                                        Please check your SMS.
                                    </p>
                                    <div className="mb-4">
                                        <label className="admin-label mb-2">AUTHORIZATION CODE</label>
                                        <div className="auth-input-wrapper">
                                            <input
                                                type="text"
                                                className="auth-input text-center"
                                                style={{ letterSpacing: '8px', fontSize: '1.2rem' }}
                                                placeholder="XXXX"
                                                maxLength="4"
                                                value={forgotOtp}
                                                onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, ''))}
                                            />
                                        </div>
                                    </div>
                                    {forgotError && <div className="admin-error-box mb-4">{forgotError}</div>}
                                    <div className="d-flex gap-2">
                                        <button className="btn-admin-outline flex-grow-1" onClick={() => { setForgotStep(1); setForgotError(''); setForgotOtp(''); }}>BACK</button>
                                        <button 
                                            className="btn-admin-submit flex-grow-1 py-3" 
                                            onClick={handleVerifyOtp}
                                            disabled={forgotLoading || forgotOtp.length < 4}
                                        >
                                            {forgotLoading ? 'VERIFYING...' : 'VERIFY CODE'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {forgotStep === 3 && (
                                <div className="fade-in">
                                    <h5 className="text-white mb-4 fw-light">STEP 03: CREDENTIAL RESTRUCTURING</h5>
                                    <p className="text-white-50 small mb-4">Verification successful. Define your new credential security key.</p>
                                    <div className="mb-3">
                                        <label className="admin-label mb-2">NEW SECURITY KEY</label>
                                        <div className="auth-input-wrapper">
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                className="auth-input"
                                                placeholder="••••••••"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                            >
                                                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mb-4">
                                        <label className="admin-label mb-2">CONFIRM SECURITY KEY</label>
                                        <div className="auth-input-wrapper">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                className="auth-input"
                                                placeholder="••••••••"
                                                value={confirmNewPsw}
                                                onChange={(e) => setConfirmNewPsw(e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>
                                    {forgotError && <div className="admin-error-box mb-4">{forgotError}</div>}
                                    {forgotSuccess && <div className="admin-success-box mb-4">{forgotSuccess}</div>}
                                    <button 
                                        className="btn-admin-submit w-100 py-3" 
                                        onClick={handleResetPassword}
                                        disabled={forgotLoading || !newPassword || newPassword !== confirmNewPsw}
                                    >
                                        {forgotLoading ? 'RESTRUCTURING...' : 'UPDATE CREDENTIALS'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .admin-login-wrapper {
                    min-height: 100vh;
                    background: radial-gradient(circle at center, #111 0%, #000 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    position: relative;
                    overflow: hidden;
                }

                .admin-login-card {
                    width: 100%;
                    max-width: 440px;
                    background: rgba(20, 20, 20, 0.6);
                    backdrop-filter: blur(25px);
                    border: 1px solid rgba(197, 160, 89, 0.1);
                    padding: 60px 45px;
                    border-radius: 4px;
                    box-shadow: 0 40px 100px rgba(0, 0, 0, 0.8);
                    z-index: 10;
                }

                .admin-label {
                    color: var(--secondary-color);
                    font-size: 0.65rem;
                    letter-spacing: 3px;
                    font-weight: 600;
                    text-transform: uppercase;
                    opacity: 0.8;
                }

                .admin-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 6px 16px;
                    background: rgba(197, 160, 89, 0.05);
                    color: #fff;
                    font-size: 0.6rem;
                    letter-spacing: 3px;
                    border: 1px solid rgba(197, 160, 89, 0.15);
                    border-radius: 2px;
                    font-weight: 500;
                    text-transform: uppercase;
                }

                .auth-input-wrapper {
                    display: flex;
                    align-items: center;
                    background: rgba(0,0,0,0.3);
                    border: 1px solid rgba(255,255,255,0.1);
                    border-radius: 2px;
                    overflow: hidden;
                }

                .auth-input {
                    flex-grow: 1;
                    background: transparent;
                    border: none;
                    color: white;
                    padding: 12px 15px;
                    font-size: 1rem;
                    outline: none;
                }

                .auth-input::placeholder {
                    color: rgba(255,255,255,0.4);
                }

                .auth-input:focus {
                    box-shadow: none;
                }

                .auth-input-wrapper:focus-within {
                    border-color: var(--secondary-color);
                }

                .phone-prefix {
                    padding: 12px 15px;
                    color: rgba(255,255,255,0.6);
                    border-right: 1px solid rgba(255,255,255,0.1);
                    font-size: 1rem;
                }

                .password-toggle-btn {
                    background: transparent;
                    border: none;
                    color: rgba(255,255,255,0.6);
                    padding: 0 15px;
                    cursor: pointer;
                    font-size: 1rem;
                    transition: color 0.2s ease;
                }

                .password-toggle-btn:hover {
                    color: var(--secondary-color);
                }

                .btn-admin-submit {
                    background: var(--secondary-color);
                    border: 1px solid var(--secondary-color);
                    color: #000;
                    font-weight: 700;
                    letter-spacing: 3px;
                    font-size: 0.75rem;
                    border-radius: 2px;
                    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
                    text-transform: uppercase;
                }

                .btn-admin-submit:hover {
                    background: transparent;
                    color: var(--secondary-color);
                    transform: translateY(-2px);
                    box-shadow: 0 10px 30px rgba(197, 160, 89, 0.15);
                }

                .admin-error-box {
                    background: rgba(220, 53, 69, 0.05);
                    border-left: 2px solid #dc3545;
                    color: #dc3545;
                    padding: 12px 15px;
                    font-size: 0.75rem;
                    letter-spacing: 1px;
                }

                .system-status-indicator {
                    font-size: 0.55rem;
                    color: rgba(255,255,255,0.3);
                    letter-spacing: 2px;
                    font-weight: 500;
                }

                .system-status-indicator::before {
                    content: '';
                    display: inline-block;
                    width: 6px;
                    height: 6px;
                    background: #28a745;
                    border-radius: 50%;
                    margin-right: 8px;
                    box-shadow: 0 0 5px #28a745;
                }

                .btn-forgot-link {
                    background: none;
                    border: none;
                    color: rgba(255,255,255,0.4);
                    font-size: 0.65rem;
                    letter-spacing: 2px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .btn-forgot-link:hover {
                    color: var(--secondary-color);
                }

                .forgot-modal {
                    max-width: 400px;
                }

                .admin-success-box {
                    background: rgba(40, 167, 69, 0.05);
                    border-left: 2px solid #28a745;
                    color: #28a745;
                    padding: 12px 15px;
                    font-size: 0.75rem;
                    letter-spacing: 1px;
                }





                .fade-in {
                    animation: fadeIn 0.4s ease-out;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .btn-admin-outline {
                    background: transparent;
                    border: 1px solid rgba(255,255,255,0.2);
                    color: white;
                    padding: 12px 24px;
                    font-size: 0.75rem;
                    letter-spacing: 2px;
                    transition: all 0.3s ease;
                }

                .btn-admin-outline:hover {
                    border-color: #fff;
                    background: rgba(255,255,255,0.05);
                }

                .admin-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.85);
                    backdrop-filter: blur(10px);
                    z-index: 10000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                }

                .admin-modal-content {
                    width: 100%;
                    background: #0a0a0a;
                    border: 1px solid rgba(255,255,255,0.1);
                    border-radius: 4px;
                    overflow: hidden;
                    box-shadow: 0 50px 100px rgba(0,0,0,0.5);
                }

                .modal-header-custom {
                    padding: 25px 30px;
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .modal-header-custom h4 {
                    font-size: 0.75rem;
                    letter-spacing: 3px;
                    font-weight: 400;
                    color: var(--secondary-color);
                }

                .btn-close-modal {
                    background: none;
                    border: none;
                    color: rgba(255,255,255,0.4);
                    font-size: 1.2rem;
                    transition: color 0.3s ease;
                }

                .btn-close-modal:hover { color: #fff; }

                .modal-body-custom {
                    padding: 40px;
                }
            `}</style>
        </div>
    );
};

export default AdminLogin;
